import { useMemo, useState } from 'react';

import { formatAxisValue, formatMetricValue, type MetricFormat } from '@/lib/metric-format';

import { ChartFrame, ChartLegend } from './chart-frame';
import {
  buildGeometry,
  buildLinePath,
  chartColor,
  formatAxisLabel,
  labelIndexes,
  shortenLabel,
  toFinite,
  type ChartSeries,
} from './chart-utils';

const WIDTH = 720;
const HEIGHT_POR_DEFECTO = 240;

interface LineChartProps {
  title?: string;
  labels: (string | number)[];
  series: ChartSeries[];
  format?: MetricFormat;
  /** Une los puntos aunque falten datos intermedios. */
  spanGaps?: boolean;
  /**
   * Oculta la leyenda interna cuando quien consume la gráfica ya muestra el
   * detalle de cada serie (evita repetir la misma lista dos veces).
   */
  showLegend?: boolean;
  /** Alto del área de dibujo; súbelo en tarjetas anchas para que no se aplaste. */
  height?: number;
}

/**
 * Gráfico de líneas multi-serie en SVG (sin dependencias).
 * Muestra guía vertical y valores al pasar el cursor.
 */
export function LineChart({
  title,
  labels,
  series,
  format = 'number',
  spanGaps = false,
  showLegend = true,
  height = HEIGHT_POR_DEFECTO,
}: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const geometry = useMemo(
    () => buildGeometry({ labelCount: labels.length, series, width: WIDTH, height }),
    [labels.length, series, height],
  );

  const paths = useMemo(
    () =>
      series.map((item, index) => {
        const points = labels.map((_, pointIndex) => {
          const value = toFinite(item.data[pointIndex]);
          return {
            x: geometry.pointX(pointIndex),
            y: value === null ? null : geometry.valueY(value),
          };
        });

        return {
          color: item.color ?? chartColor(index),
          label: item.label,
          data: item.data,
          ...buildLinePath(points, spanGaps),
        };
      }),
    [geometry, labels, series, spanGaps],
  );

  const visibleLabels = labelIndexes(labels.length, 7);
  const legendItems = series.map((item, index) => ({ label: item.label, color: chartColor(index) }));

  return (
    <ChartFrame
      title={title}
      legend={showLegend && legendItems.length > 1 ? <ChartLegend items={legendItems} /> : null}
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        role="img"
        aria-label={title ?? 'Gráfico de líneas'}
        className="h-auto w-full"
      >
        {/* Rejilla y marcas del eje Y */}
        {geometry.ticks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={geometry.padding.left}
              x2={WIDTH - geometry.padding.right}
              y1={tick.y}
              y2={tick.y}
              stroke="#e6edf1"
              strokeDasharray={tick.value === 0 ? undefined : '3 4'}
            />
            <text
              x={geometry.padding.left - 8}
              y={tick.y + 3.5}
              textAnchor="end"
              className="fill-ink-400 text-[10px]"
            >
              {formatAxisValue(tick.value, format)}
            </text>
          </g>
        ))}

        {/* Etiquetas del eje X */}
        {visibleLabels.map((index) => (
          <text
            key={index}
            x={geometry.pointX(index)}
            y={height - 10}
            textAnchor="middle"
            className="fill-ink-400 text-[10px]"
          >
            {formatAxisLabel(labels[index])}
          </text>
        ))}

        {/* Líneas */}
        {paths.map((item) => (
          <path
            key={item.label}
            d={item.path}
            fill="none"
            stroke={item.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            className="[vector-effect:non-scaling-stroke]"
          />
        ))}

        {/* Puntos + interacción */}
        {labels.map((label, index) => {
          const isHovered = hover === index;

          return (
            <g
              key={`point-${String(label)}-${index}`}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            >
              {/* Zona de interacción */}
              <rect
                x={geometry.pointX(index) - geometry.innerWidth / Math.max(labels.length, 1) / 2}
                y={geometry.padding.top}
                width={geometry.innerWidth / Math.max(labels.length, 1)}
                height={geometry.innerHeight}
                fill="transparent"
              />

              {isHovered ? (
                <line
                  x1={geometry.pointX(index)}
                  x2={geometry.pointX(index)}
                  y1={geometry.padding.top}
                  y2={height - geometry.padding.bottom}
                  stroke="#a0bccb"
                  strokeDasharray="3 3"
                />
              ) : null}

              {paths.map((item) => {
                const value = toFinite(item.data[index]);
                if (value === null) return null;

                return (
                  <circle
                    key={`${item.label}-${index}`}
                    cx={geometry.pointX(index)}
                    cy={geometry.valueY(value)}
                    r={isHovered ? 4.5 : 3}
                    fill="#ffffff"
                    stroke={item.color}
                    strokeWidth={2}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hover !== null && labels[hover] !== undefined ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-ink-50 px-2.5 py-1.5 text-[11px]">
          <span className="font-semibold text-ink-700">{shortenLabel(labels[hover], 22)}</span>

          {series.map((item, index) => (
            <span key={item.label} className="flex items-center gap-1.5 text-ink-600">
              <span
                aria-hidden="true"
                className="size-2 rounded-full"
                style={{ backgroundColor: chartColor(index) }}
              />
              {item.label}:{' '}
              <strong className="font-mono font-semibold text-ink-800">
                {formatMetricValue(toFinite(item.data[hover]) ?? 0, format)}
              </strong>
            </span>
          ))}
        </div>
      ) : null}
    </ChartFrame>
  );
}
